import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, PageBlock } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { Collection, DevBar } from '@eti/debug';
import { useHeightContext, useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import {
  BoundNew,
  BrandedFare,
  BundleProduct,
  ExtraProduct,
  TravelerNew,
  TravelerPrice,
  TripChange,
  TripCharacteristics,
} from '@eti/types';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Media from 'react-media';
import { useMediaQuery } from 'usehooks-ts';
import { analytics, price as priceUtil } from '@eti/utils';
import { flexibleTicket } from '@eti/products';
import { Tier } from '@eti/schema-types';
import SelectedTrip from '../../result/components/v1/SelectedTrip';
import StepIndicator from '../../../common/step-indicator/BookingFlowStepIndicator';
import { SELECT_FLIGHT } from '../../../constants/bookingFlowSteps';
import { isDev } from '../../../common/debug/utils';
import { DebugCommonComponents, DebugGraphQlResponses } from '../../../common/debug/components';
import { routes } from '../../../constants/routesAndApisConstants';
import { getTicketingCarrierCodes } from '../../traveler-details/utils/carriers';
import { MULTI_STOP, ONE_WAY, RETURN } from '../../../constants/tripTypesConstants';
import { getUniqueMarketingCarriers } from '../../../utils/carriers';
import DifferentChargingCurrencyNotification from '../../../widgets/different-charging-currency-notification/DifferentChargingCurrencyNotification';
import { useScrollTop } from '../../../common/custom-hooks/scrolling';
import { useScrollIntoVisibleViewport } from '../../../utils/useScrollIntoVisibleViewport';
import { useTravelerPricesToDataLayerEffect } from '../../../common/data-layer/dataLayer';
import { getOriginAndDestinationCodesFromBound } from '../../../utils/format';
import { isTicketIncluded } from '../../result/utils/resultUtils';
import { useGetBlue, eventTypes } from '../../../utils/useGetBlue';
import { getDefaultAndRecommendedFare } from './v2/BrandedFares';
import AncillaryBundleSelection from './AncillaryBundleSelection';
import TripDetailsTitle from './v3/TripDetailsTitle';
import SelectedTripCard from './selected-trip-card/v3/SelectedTripCard';
import StickyFooter from './sticky-footer/StickyFooter';
import { SellingPoint } from './selling-point/SellingPoint';
import {
  getBrandedFaresComponent,
  getInitialBrandedFareSelectionKey,
  getSelectedTripPricePerAdult,
} from './commonUtils';
import PriceChangeNotification from './PriceChangeNotification';
import ProceedButton from './ProceedButton';

export const buttonStyles = css`
  width: 100%;

  @media ${mediaQueries.medium.up} {
    width: auto;
  }
`;

const wrapperStyles = (
  isMobileDevice = false,
  shouldDisplayAncillaryBundle,
  shouldDisplayEtgFares,
  headerAndStepIndicatorHeight,
  shouldShowStickyFooter,
) => {
  const height = isMobileDevice ? '100dvh' : '100vh';
  return css`
    background-color: ${shouldDisplayAncillaryBundle && !shouldDisplayEtgFares
      ? etiColors.white
      : etiColors.grayScale[4]};
    display: flex;
    flex-direction: column;
    height: 100%;
    ${shouldShowStickyFooter && `min-height: calc(${height} - ${headerAndStepIndicatorHeight}px);`}
  `;
};

const contentStyles = css`
  margin: 2rem auto 1rem;
`;

const headingStyles = css`
  color: ${etiColors.black};
  font-size: 1.375rem;
  text-align: center;
`;

const sectionGFSWrapperNoBrandedFaresStyles = css`
  margin: 10px auto 0;

  @media ${mediaQueries.medium.up} {
    margin: 24px auto 0;
  }
`;

const sectionTitleWrapperStyles = css`
  margin: 10px auto;

  @media ${mediaQueries.medium.up} {
    margin: 24px auto 0;
  }
`;

const getBasicBundleOption = (bundleData) => {
  return bundleData?.options?.find((option) => option.type === Tier.Basic) ?? {};
};

const YourSelectedTripPage = ({
  addTrip,
  availableExtraProducts,
  bounds,
  brandedFares = [],
  bundleData,
  isSelectedTripRefetching,
  hasBookingButton,
  includedCabinBaggage,
  includedCheckedBaggage,
  isMultiStop,
  isSearchLoading,
  isSelectTripLoading,
  numberOfTravelers,
  paymentMethodPrices,
  redirectToTravelerDetails,
  selectedBundleId,
  selectedBundlePrice,
  selectedExtraProducts,
  selectionKey,
  selectTrip,
  setSelectedBundleOption,
  shouldHideSelectedTrip,
  systems,
  travelerPrices,
  travelerPricesWithoutPaymentDiscounts,
  tripChange,
  tripCharacteristics,
  tripTravelers,
  type,
  voucherAmount,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { p } = useProperty();
  const [selectedBrandedFareSelectionKey, setSelectedBrandedFareSelectionKey] = useState(
    getInitialBrandedFareSelectionKey(brandedFares, selectionKey),
  );

  useTravelerPricesToDataLayerEffect(travelerPrices, paymentMethodPrices);

  const { partner } = useSiteContext();
  const brandedFaresVersion = p('YourSelectedTrip.Page.BrandedFares.Version');

  const isSelectedTripCardEnabled = p('YourSelectedTrip.Page.SelectedTripCard.V3.Enabled');
  const isSelectedTripCardEnabledForBrandedFareV3 =
    p('YourSelectedTrip.Page.BrandedFares.V3.SelectedTripCard.V3.Enabled') &&
    brandedFaresVersion === 3;

  const isSelectedTripCardEnabledForETGFares = p(
    'YourSelectedTrip.Page.ETGFares.SelectedTripCard.V3.Enabled',
  );
  const isBrandedFaresEnabled = p('BrandedFares.Enabled');
  const isAncillaryBundleEnabled = p('YourSelectedTrip.AncillaryBundle.Enabled');
  const isETGFaresEnabled = p('ETG.Fares.Enabled');
  const yourSelectedTripPageVersion = p('YourSelectedTrip.Page.Version');
  const isDifferentChargingCurrencyNotificationEnabled = p(
    'IbeClient.DifferentChargingCurrencyNotification.Enabled',
  );
  const isDynamicBookingFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled');

  const shouldDisplayBrandedFares = isBrandedFaresEnabled && brandedFares?.length > 0;

  const shouldDisplayAncillaryBundle =
    !shouldDisplayBrandedFares && isAncillaryBundleEnabled && bundleData;

  const shouldDisplayEtgFares = shouldDisplayAncillaryBundle && isETGFaresEnabled;

  const { defaultSelectedFareKey, recommendedFareKey } =
    (shouldDisplayBrandedFares && getDefaultAndRecommendedFare(travelerPrices, brandedFares)) || [];

  const isYourSelectedTripPageV2Enabled = yourSelectedTripPageVersion === 2;
  const isYourSelectedTripPageV1Enabled = yourSelectedTripPageVersion === 1;

  const isFareAvailable =
    shouldDisplayBrandedFares || shouldDisplayEtgFares || shouldDisplayAncillaryBundle;

  const eitherETGFaresOrBrandedFaresEnableAndAvailable =
    (isSelectedTripCardEnabledForBrandedFareV3 && shouldDisplayBrandedFares) ||
    (isSelectedTripCardEnabledForETGFares &&
      (shouldDisplayEtgFares || shouldDisplayAncillaryBundle));

  const shouldDisplaySelectedTripCard =
    (eitherETGFaresOrBrandedFaresEnableAndAvailable || !isFareAvailable) &&
    isSelectedTripCardEnabled;

  const skipToTDPageWrtSelectedTripCardFlags =
    !isSelectedTripCardEnabled &&
    !shouldDisplayBrandedFares &&
    !shouldDisplayEtgFares &&
    !shouldDisplayAncillaryBundle;

  const shouldDisplayProceedButton = shouldDisplayAncillaryBundle && !shouldDisplayEtgFares;

  const shouldDisplayTripDetailsTitle =
    ![1, 2].includes(brandedFaresVersion) && (shouldHideSelectedTrip || isFareAvailable);

  const basicBundleOption = getBasicBundleOption(bundleData);

  const callSelectTripAndProceed = async (
    bundlePrice,
    bundleId,
    isFareSelected,
    productIdsToPurchase,
    shouldRedirectToDbf,
  ) => {
    if (isFareSelected) {
      await selectTrip({
        variables: {
          selectionKey: selectedBrandedFareSelectionKey || defaultSelectedFareKey || selectionKey,
          ...(bundlePrice && {
            bundleProductSelection: [{ bundleId: bundleData.id, optionId: bundleId }],
          }),
          extraProductSelection: productIdsToPurchase,
        },
      });
    }

    if (shouldRedirectToDbf) {
      return history.push(routes.STEP_TWO);
    }
    return history.push(routes.TRAVELER_DETAILS);
  };

  const handleProceedButtonClick = (productIdsToPurchase) => {
    const brandedFareSelected =
      shouldDisplayBrandedFares &&
      (selectedBrandedFareSelectionKey || defaultSelectedFareKey || selectionKey);
    const shouldRedirectToDbf =
      isDynamicBookingFlowEnabled && !isTicketIncluded(availableExtraProducts, flexibleTicket.id);
    const isFareSelected = brandedFareSelected || Boolean(selectedBundleId);
    callSelectTripAndProceed(
      selectedBundlePrice,
      selectedBundleId,
      isFareSelected,
      productIdsToPurchase,
      shouldRedirectToDbf,
    );
  };

  const handleSkipStepButtonClick = () => {
    const shouldRedirectToDbf =
      isDynamicBookingFlowEnabled && !isTicketIncluded(availableExtraProducts, flexibleTicket.id);
    callSelectTripAndProceed(
      basicBundleOption.priceRaw,
      basicBundleOption.id,
      true,
      selectedExtraProducts,
      shouldRedirectToDbf,
    );
  };

  const headingText =
    t(`YourSelectedTrip.BookingFlow.PartnerName.${partner}`) ||
    t('YourSelectedTrip.Page.NoPartner.Heading');

  analytics.useLogMouseFlowTagEffect(
    'Branded Fares',
    shouldDisplayBrandedFares && brandedFaresVersion === 2,
  );

  const isSelectTripEnabled = !shouldHideSelectedTrip && bounds?.length > 0;

  const isMobileDevice = useMediaQuery(`(max-width: ${breakpoints._768})`);
  const shouldHideUniqueSellingPoint = shouldDisplaySelectedTripCard && !isMobileDevice;

  const { headerHeightContext } = useHeightContext();
  const stepIndicatorHeight = 120;
  const headerAndStepIndicatorHeight = headerHeightContext + stepIndicatorHeight;
  useScrollIntoVisibleViewport({ paddingTop: 10, paddingBottom: 16 });

  const shouldShowStickyFooter = !isFareAvailable;

  useGetBlue(eventTypes.PRODUCT, getOriginAndDestinationCodesFromBound(bounds[0]));

  useEffect(() => {
    if (
      shouldHideSelectedTrip &&
      !shouldDisplayBrandedFares &&
      !shouldDisplaySelectedTripCard &&
      skipToTDPageWrtSelectedTripCardFlags
    ) {
      if (
        isDynamicBookingFlowEnabled &&
        !isTicketIncluded(availableExtraProducts, flexibleTicket.id)
      ) {
        history.push(routes.STEP_TWO);
        return;
      }
      history.push(routes.TRAVELER_DETAILS);
    }
  }, [
    shouldHideSelectedTrip,
    shouldDisplayBrandedFares,
    shouldDisplaySelectedTripCard,
    skipToTDPageWrtSelectedTripCardFlags,
    history,
    isDynamicBookingFlowEnabled,
    availableExtraProducts,
  ]);

  useScrollTop();

  const BrandedFares = getBrandedFaresComponent(brandedFaresVersion);
  const selectedTripPricePerAdult = getSelectedTripPricePerAdult(tripTravelers, travelerPrices);

  return (
    <>
      <Helmet title={t('PageTitle.rf.your-selected-trip.action')} />
      <StepIndicator currentStep={SELECT_FLIGHT} />
      <section
        className={wrapperStyles(
          isMobileDevice,
          shouldDisplayAncillaryBundle,
          shouldDisplayEtgFares,
          headerAndStepIndicatorHeight,
          shouldShowStickyFooter,
        )}
      >
        {isDifferentChargingCurrencyNotificationEnabled && (
          <ContentWrapper>
            <DifferentChargingCurrencyNotification className={contentStyles} />
          </ContentWrapper>
        )}
        {!shouldHideSelectedTrip && (
          <ContentWrapper className={contentStyles}>
            <h3 className={headingStyles} data-testid="yourSelectedTrip-heading">
              {headingText}
            </h3>
          </ContentWrapper>
        )}
        <PriceChangeNotification tripChange={tripChange} />
        {isSelectTripEnabled && (
          <PageBlock as="section" data-testid="resultPage-selectedTrip-wrapper" withPaddingY>
            <SelectedTrip
              addTrip={addTrip}
              availableExtraProducts={availableExtraProducts}
              bounds={bounds}
              hasBookingButton={hasBookingButton}
              includedCheckedBaggage={includedCheckedBaggage}
              isMultiStop={isMultiStop}
              isSearchLoading={isSearchLoading}
              isSelectTripLoading={isSelectTripLoading}
              isSelectedTripRefetching={isSelectedTripRefetching}
              numberOfTravelers={numberOfTravelers}
              paymentMethodPrices={paymentMethodPrices}
              redirectToTravelerDetails={redirectToTravelerDetails}
              selectionKey={selectionKey}
              shouldDisplayBrandedFares={shouldDisplayBrandedFares}
              systems={systems}
              travelerPrices={travelerPrices}
              travelerPricesWithoutPaymentDiscounts={travelerPricesWithoutPaymentDiscounts}
              tripCharacteristics={tripCharacteristics}
              tripTravelers={tripTravelers}
              voucherAmount={voucherAmount}
            />
          </PageBlock>
        )}
        {isYourSelectedTripPageV2Enabled && (
          <ContentWrapper className={sectionGFSWrapperNoBrandedFaresStyles}>
            {(shouldDisplaySelectedTripCard || shouldDisplayTripDetailsTitle) && (
              <TripDetailsTitle bounds={bounds} />
            )}
            {shouldDisplaySelectedTripCard && (
              <SelectedTripCard
                availableExtraProducts={availableExtraProducts}
                bounds={bounds}
                includedCabinBaggage={includedCabinBaggage}
                includedCheckedBaggage={includedCheckedBaggage}
                selectedTripPricePerAdult={selectedTripPricePerAdult}
                tripCharacteristics={tripCharacteristics}
              />
            )}
          </ContentWrapper>
        )}

        {isYourSelectedTripPageV1Enabled && shouldDisplayEtgFares && (
          <ContentWrapper className={sectionTitleWrapperStyles}>
            <TripDetailsTitle bounds={bounds} />
          </ContentWrapper>
        )}

        {shouldDisplayBrandedFares && (
          <section>
            <Media query={mediaQueries.medium.only}>
              {(isMediumScreenWidth) => (
                <BrandedFares
                  availableExtraProducts={availableExtraProducts}
                  bounds={bounds}
                  brandedFares={brandedFares}
                  defaultSelectedFareKey={defaultSelectedFareKey}
                  discountAmount={voucherAmount?.value ?? 0}
                  handleProceedButtonClick={handleProceedButtonClick}
                  isMediumScreenWidth={isMediumScreenWidth}
                  isSelectTripEnabled={isSelectTripEnabled}
                  isSelectTripLoading={isSelectTripLoading}
                  isSelectedTripRefetching={isSelectedTripRefetching}
                  marketingCarrier={getUniqueMarketingCarriers(bounds)[0]}
                  partner={partner}
                  paymentMethodPrices={paymentMethodPrices}
                  recommendedFareKey={recommendedFareKey}
                  selectedBrandedFareSelectionKey={selectedBrandedFareSelectionKey}
                  selectedTripPricePerAdult={selectedTripPricePerAdult}
                  setSelectedBrandedFareSelectionKey={setSelectedBrandedFareSelectionKey}
                  ticketingCarrierCodes={getTicketingCarrierCodes(bounds)}
                  travelerPrices={travelerPrices}
                  travelerPricesWithoutPaymentDiscounts={travelerPricesWithoutPaymentDiscounts}
                  tripTravelers={tripTravelers}
                  tripType={type}
                />
              )}
            </Media>
          </section>
        )}
        {shouldDisplayAncillaryBundle && (
          <AncillaryBundleSelection
            ancillaryBundleId={bundleData.id}
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            discountAmount={voucherAmount?.value ?? 0}
            discountPercentage={bundleData?.discountPercentage}
            handleProceedButtonClick={() => handleProceedButtonClick(selectedExtraProducts)}
            onSkipStepClick={handleSkipStepButtonClick}
            options={bundleData?.options}
            paymentMethodPrices={paymentMethodPrices}
            selectedBundleId={selectedBundleId}
            selectedExtraProducts={selectedExtraProducts}
            setSelectedBundleOption={setSelectedBundleOption}
            shouldDisplayEtgFares={shouldDisplayEtgFares}
            travelerPrices={travelerPrices}
            travelerPricesWithoutDiscounts={travelerPricesWithoutPaymentDiscounts}
            tripTravelers={tripTravelers}
            tripType={type}
          />
        )}
        {shouldDisplayProceedButton && (
          <ProceedButton
            handleProceedButtonClick={handleProceedButtonClick}
            selectedBundleId={selectedBundleId}
            selectedExtraProducts={selectedExtraProducts}
            shouldDisplayAncillaryBundle={shouldDisplayAncillaryBundle}
          />
        )}
      </section>
      <section>
        {shouldShowStickyFooter && (
          <StickyFooter
            bounds={bounds}
            discountAmount={voucherAmount?.value ?? 0}
            isSelectTripLoading={isSelectTripLoading}
            onClick={() => handleProceedButtonClick(selectedExtraProducts)}
            paymentMethodPrices={paymentMethodPrices}
            totalPrice={priceUtil.formatPrice(selectedTripPricePerAdult)}
            travelerPrices={travelerPrices}
            travelerPricesWithoutDiscounts={travelerPricesWithoutPaymentDiscounts}
            tripTravelers={tripTravelers}
          />
        )}

        {!shouldHideUniqueSellingPoint && (
          <SellingPoint shouldDisplayMainUsp={!shouldHideSelectedTrip} />
        )}
      </section>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugGraphQlResponses />
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

YourSelectedTripPage.propTypes = {
  addTrip: PropTypes.func,
  availableExtraProducts: PropTypes.arrayOf(ExtraProduct),
  bounds: PropTypes.arrayOf(BoundNew),
  brandedFares: PropTypes.arrayOf(BrandedFare),
  bundleData: BundleProduct,
  hasBookingButton: PropTypes.bool,
  includedCabinBaggage: PropTypes.shape({
    includedPersonalItem: PropTypes.bool, // eslint-disable-line
    size3d: PropTypes.shape({
      height: PropTypes.number,
      length: PropTypes.number,
      width: PropTypes.number,
    }),
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  includedCheckedBaggage: PropTypes.shape({
    pieces: PropTypes.number,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  isMultiStop: PropTypes.bool,
  isSearchLoading: PropTypes.bool,
  isSelectedTripRefetching: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  numberOfTravelers: PropTypes.number,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
  ),
  redirectToTravelerDetails: PropTypes.func,
  selectedBundleId: PropTypes.string,
  selectedBundlePrice: PropTypes.number,
  selectedExtraProducts: PropTypes.arrayOf(PropTypes.number),
  selectionKey: PropTypes.string,
  selectTrip: PropTypes.func,
  setSelectedBundleOption: PropTypes.func,
  shouldHideSelectedTrip: PropTypes.bool,
  systems: PropTypes.arrayOf(PropTypes.string),
  travelerPrices: PropTypes.arrayOf(TravelerPrice),
  travelerPricesWithoutPaymentDiscounts: PropTypes.arrayOf(TravelerPrice),
  tripChange: TripChange,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripTravelers: PropTypes.arrayOf(TravelerNew),
  type: PropTypes.oneOf([MULTI_STOP, ONE_WAY, RETURN]),
  voucherAmount: PropTypes.shape({
    value: PropTypes.number,
  }),
};

export default YourSelectedTripPage;
